<template>
  <Component
    :is="maxSmall ? Drawer : Dialog"
    @update:open="handleUpdateOpen"
    v-bind="maxSmall ? {} : { size: 'full', side: 'default' }"
    :title="$t('components.profile.password.title')"
    :description="$t('components.profile.password.description')"
    :teleport="maxSmall"
    showFooter
  >
    <slot />
    <template #header>
      <Component
        :is="maxSmall ? DrawerHeader : DialogHeader"
        :title="$t('components.profile.password.title')"
        v-bind="
          maxSmall
            ? {
                variant: 'tertiary',
                type: 'gray',
                size: 'xl',
              }
            : {
                description: $t('components.profile.password.description'),
              }
        "
        class="bg-primary md:p-6 md:pb-5 border-b border-secondary"
      />
    </template>
    <template #content>
      <div class="px-5 md:px-6 bg-primary overflow-y-auto h-full">
        <div class="flex flex-col pt-4 gap-2 mb-4">
          <PasswordInput
            :label="$t('components.profile.password.current.label')"
            v-model="currentPassword"
            :autocomplete="false"
            :errorMessage="errors.password?.[0]"
            :showPasswordButton="!maxSmall"
            :size="maxSmall ? 'lg' : 'sm'"
          />
          <PasswordInput
            :label="$t('components.profile.password.new.label')"
            v-model="newPassword"
            aria-autocomplete="none"
            :errorMessage="errors.new_password?.[0]"
            :showPasswordButton="!maxSmall"
            :size="maxSmall ? 'lg' : 'sm'"
          />
          <PasswordInput
            :label="$t('components.profile.password.confirm.label')"
            v-model="confirmPassword"
            aria-autocomplete="none"
            :size="maxSmall ? 'lg' : 'sm'"
            :showPasswordButton="!maxSmall"
            :errorMessage="errors.new_password_confirmation?.[0]"
            class="pb-4"
          />
        </div>
      </div>
    </template>
    <template #footer>
      <div
        class="flex flex-col bg-primary py-4 px-4 md:px-6 gap-3 max-md:border-t border-secondary"
      >
        <OptIn
          v-if="!maxSmall"
          :label="$t('components.profile.password.logout.header')"
          :hint="$t('components.profile.password.logout.hint')"
          type="checkbox"
          class="mb-4"
          v-model:checked="logout"
        />
        <Button
          :as="DialogClose"
          :label="$t('components.profile.password.confirm.label')"
          size="xl"
          class="w-full"
          @click="changePassword"
          :disabled="busy || noChanges"
        />
        <Button
          v-if="!maxSmall"
          :as="DialogClose"
          :label="$t('components.buttons.cancel')"
          size="xl"
          class="w-full"
          variant="secondary"
          type="gray"
          @click.stop
        />
      </div>
    </template>
  </Component>
</template>
<script setup>
import { ref, computed } from "vue";
import { Button } from "@/SharedComponents/Buttons";
import { Dialog, DialogClose, DialogHeader } from "@/SharedComponents/Dialog";
import { Drawer, DrawerHeader } from "@/SharedComponents/Drawer";
import { PasswordInput } from "@/SharedComponents/Inputs";
import { OptIn } from "@/SharedComponents/Switchers";
import { useBreakpoints } from "@/Composables/useBreakpoints";

const { maxSmall } = useBreakpoints();

const emit = defineEmits(["changePassword", "clearErrors"]);

defineProps({
  user: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
  busy: { type: Boolean, default: false },
});

const currentPassword = ref("");
const newPassword = ref("");
const confirmPassword = ref("");
const logout = ref(false);

function handleUpdateOpen(open) {
  if (open) {
    reset();
    emit("clearErrors");
  }
}

const reset = () => {
  currentPassword.value = "";
  newPassword.value = "";
  confirmPassword.value = "";
  logout.value = false;
};

const changePassword = () => {
  emit("clearErrors");
  emit("changePassword", {
    password: currentPassword.value,
    new_password: newPassword.value,
    new_password_confirmation: confirmPassword.value,
    logout: logout.value,
  });
};

const noChanges = computed(() => {
  return !currentPassword.value || !newPassword.value || !confirmPassword.value;
});
</script>
